import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AuthService } from "@/services/authService";
import { decodeToken, needsRenewal } from "@/lib/tokenSession";
import wsClient from "@/lib/websocket";
// DISABLED-FEATURE: browser extension is switched off — see server/app/api/v2/api.py.
// import { ExtensionService } from "@/services/extensionService";
import { PERMISSIONS } from "@/permissions/constant";
const initialUser = {
  name: "",
  email: "",
  employeeId: "",
  id: "",
  role: "",
  roleId: null,
  permissions: [],
  isPending: false,
  isSuperAdmin: false,
  isTenantAdmin: false,
  tenantSlug: "",
  // Module entitlements — what this tenant's plan includes. Drives visibility
  // for features that own no permission code of their own (the AI tool panels),
  // which permission filtering alone cannot hide.
  enabledModules: [],
  // Modules currently switched off fleet-wide. Deliberately SEPARATE from
  // `enabledModules`: a module the tenant owns but that is temporarily dark has
  // to look different from one they never bought — visible and clickable, with
  // existing data intact and only the action controls disabled.
  unavailableModules: [],
  // Reconciliation counters. Compared against the `X-Config-Version` response
  // header; a higher header value means our permissions are stale.
  configVersion: 0,
  platformConfigVersion: 0,
  // SuperAdmin-controlled entitlement — read-only here. `extensionEnabled`
  // is false whenever `enabledExtensionSites` is empty (or the tenant is
  // inactive); the two are kept as separate fields so the UI can say
  // "no sites enabled" instead of just "not available".
  extensionEnabled: false,
  enabledExtensionSites: [],
  // SuperAdmin-controlled sourcing entitlement — read-only here, same as the
  // extension fields above. True = this tenant brings its own sourcing platform
  // API keys and gets the "Configure APIs" screen (subject to
  // campaign.credentials_manage). False = it transparently uses the platform's
  // keys and the screen does not exist for it.
  //
  // Used only to hide the route. The API enforces the real gate, so a stale value
  // here cannot grant access.
  sourcingUsesOwnApiKeys: false,
  // Whether THIS TENANT signs in through Active Directory — the tenant's own
  // flag, already ANDed with the deployment's corporate-login switch
  // server-side, so it is the effective answer rather than one half of it.
  // True = employees sign in with an employee ID against the corporate
  // directory, accounts appear on first sign-in, and manual invites are
  // refused.
  //
  // Read-only and advisory, like the two entitlements above: it decides which
  // controls a screen offers, and the API enforces the real gate.
  adLoginEnabled: false,
  // How THIS ROW authenticates — "ad" or "local". Deliberately separate from
  // the tenant flag above: an invited local user on a tenant that later turned
  // AD on still has a password, so the profile page's password card follows
  // this and not `adLoginEnabled`.
  authProvider: null,
};

const initialState = {
  isAuthenticated: false,
  // Blocking load: nobody can be rendered yet, because we do not know who the
  // user is. `AuthGuard` shows a full-page loader and `RouteGuard` renders
  // null while this is true, so the whole route subtree is unmounted.
  isLoading: false,
  // Background refetch of an ALREADY known user. Never blocks a render. See
  // the note on `currentUser.pending`.
  isRefreshing: false,
  authChecked: false,
  user: initialUser,
  error: null,
};

/**
 * Whether this user is a tenant admin now comes from the server, as an
 * explicit `isTenantAdmin` on the /auth/current-user response.
 *
 * There used to be a client-side `isTenantAdminToken()` here, used to widen an
 * empty permissions array to the `*` wildcard as a safety net: if the backend
 * returned nothing for someone the token identified as a tenant admin, the
 * sidebar would filter itself away and the tenant would be unadministerable.
 *
 * That widening is deliberately gone. Permissions are now filtered by module
 * entitlement, so **an empty list is a legitimate state** — a tenant entitled
 * to very little has a legitimately near-empty admin UI. Widening to `*` would
 * hand that admin the entire interface, producing exactly the outcome module
 * entitlement exists to prevent. (Not a security hole either way — the backend
 * module gate refuses every call regardless — but it is the wrong thing to
 * show.) The server owns the answer completely; the frontend stops guessing.
 */

const normalizeCurrentUser = (data) => {
  const currentData = data?.data || data?.user || data || {};

  // The response body wins over the JWT for both flags.
  //
  // The token is only as fresh as the last sign-in or renewal; this endpoint
  // resolves the user from the database on every call. Reading `isPending`
  // from the token meant an admin could assign someone a role and that person
  // stayed locked on the "pending access" screen until they signed out and in
  // again. The token is still the fallback for a payload that omits the field.
  //
  // `decodeToken` is used rather than `jwtDecode` because it returns null on a
  // missing or malformed token instead of throwing — this runs on the bootstrap
  // path, where there may be no token at all yet.
  const decodedToken = decodeToken(AuthService.getAccessToken()) || {};

  const isSuperAdmin = !!(currentData?.isSuperAdmin ?? currentData?.is_super_admin ?? decodedToken?.isSuperAdmin);
  const isPending = !!(currentData?.isPending ?? decodedToken?.isPending);

  const userRole = isSuperAdmin ? "Super Admin" : currentData?.role?.name || currentData?.role_name || null;
  // The JWT carries the role as `{ id, name, ... }` — keep the id so the
  // Roles page can disable Edit/Delete on the row matching the current user.
  const userRoleId = (typeof currentData?.role === "object" && currentData?.role?.id) || (typeof decodedToken?.role === "object" && decodedToken?.role?.id) || null;

  return {
    isActive: !!currentData?.is_active,
    user: {
      name: currentData?.name || currentData?.email || currentData?.username || "",
      email: currentData?.email || "",
      employeeId: currentData?.id || null,
      id: currentData?.id || null,
      role: userRole,
      roleId: userRoleId,
      // Live from the /auth/current-user response body, not the JWT — the
      // JWT is only as fresh as the last login/refresh, but this endpoint
      // resolves the user's role/permissions from the DB on every call. That
      // is what makes a module or permission change apply without re-login.
      //
      // Already filtered by module entitlement server-side. An empty array is
      // taken at face value — see the note above normalizeCurrentUser.
      //
      // isPending does NOT swap in a synthetic permission set any more. An
      // AD-provisioned user holds a real default role from creation (see
      // get_or_create_user on the server), so `currentData.permissions`
      // already carries their real grants — same as everyone else.
      permissions: isSuperAdmin
        ? [PERMISSIONS.SUPER_ADMIN_MANAGE]
        : Array.isArray(currentData?.permissions)
          ? currentData.permissions
          : [],
      isPending: isPending,
      isSuperAdmin: isSuperAdmin,
      isTenantAdmin: !!currentData?.isTenantAdmin,
      tenantSlug: currentData?.tenantSlug || "",
      enabledModules: Array.isArray(currentData?.enabledModules)
        ? currentData.enabledModules
        : // Absent (an older backend, or the SuperAdmin payload) → treat every
          // module as owned. Fail open, matching the server: a transient gap in
          // this list must not make a paying client's features vanish.
          null,
      unavailableModules: Array.isArray(currentData?.unavailableModules) ? currentData.unavailableModules : [],
      configVersion: Number(currentData?.configVersion ?? 0),
      platformConfigVersion: Number(currentData?.platformConfigVersion ?? 0),
      // SuperAdmin-controlled entitlements, read-only here — the browser
      // extension sites and the sourcing bring-your-own-keys switch. Separate
      // from `enabledModules` above: those are plan entitlements resolved by
      // the module registry, these are per-tenant switches on the tenant row.
      extensionEnabled: !!currentData?.extensionEnabled,
      enabledExtensionSites: Array.isArray(currentData?.enabledExtensionSites) ? currentData.enabledExtensionSites : [],
      sourcingUsesOwnApiKeys: !!currentData?.sourcingUsesOwnApiKeys,
      // The tenant's AD sign-in answer and this row's own provider — see the
      // notes on `initialUser`. Absent means email sign-in: the arrangement
      // every tenant had before the flag existed, and the only safe default,
      // since reading absence as AD would hide the password card and the
      // invite button from every tenant at once.
      adLoginEnabled: !!currentData?.adLoginEnabled,
      authProvider: currentData?.authProvider || currentData?.auth_provider || null,
    },
  };
};

export const currentUser = createAsyncThunk("auth/current_user", async (_, { rejectWithValue }) => {
  try {
    const data = await AuthService.currentUser();
    return normalizeCurrentUser(data);
  } catch (error) {
    // The status rides on the payload because the reducer's decision turns on
    // it: 401 is the only failure that means "you are not signed in", and
    // every other one has to leave the session alone (see `currentUser
    // .rejected`). Without it the reducer sees a bare message and cannot tell
    // a throttled read from a dead token.
    return rejectWithValue({
      status: error?.response?.status ?? null,
      ...(error?.response?.data || { message: error?.message || "Auth check failed" }),
    });
  }
});

export const login = createAsyncThunk("auth/login", async ({ mode, email, employeeId, password, acknowledged }, { rejectWithValue, dispatch }) => {
  try {
    if (mode === "local") {
      await AuthService.signinLocal({ email, password, acknowledged });
    } else {
      await AuthService.login(employeeId, password, acknowledged);
    }

    const resultAction = await dispatch(currentUser());

    if (currentUser.rejected.match(resultAction)) {
      AuthService.clearAuthStorage();
      return rejectWithValue(resultAction.payload || { message: "Failed to fetch current user" });
    }

    return resultAction.payload;
  } catch (error) {
    AuthService.clearAuthStorage();
    return rejectWithValue(error?.response?.data || { message: error?.message || "Login failed" });
  }
});

export const acceptInvite = createAsyncThunk("auth/accept", async ({ token, password }, { rejectWithValue, dispatch }) => {
  try {
    const res = await AuthService.acceptInvite({ token, password });
    const userData = normalizeCurrentUser(res.user);

    return userData;
  } catch (error) {
    AuthService.clearAuthStorage();
    return rejectWithValue(error?.response?.data || { message: error?.message || "Login failed" });
  }
});

/**
 * Sign out.
 *
 * The socket is closed first and unconditionally. It authenticates with the
 * access token in its URL and stays open until something closes it, so a
 * sign-out that only cleared storage left a live connection streaming this
 * tenant's events to a browser nobody is signed in on any more.
 *
 * The server answers 200 even for a cookie it does not recognise, so reaching
 * the rejected branch means the request never landed. Local state is cleared
 * either way — a user who asked to sign out must not be left holding a token,
 * whatever the network did.
 */
export const logout = createAsyncThunk("auth/logout", async (_, { rejectWithValue }) => {
  wsClient.disconnect();
  try {
    await AuthService.logout();
    return { success: true };
  } catch (error) {
    console.error("[Auth] Logout failed on server:", error?.response?.data || error?.message);
    AuthService.clearAuthStorage();
    return rejectWithValue(error?.response?.data || { message: error?.message || "Logout failed" });
  }
});

/**
 * Decide, once per page load, whether this browser still has a session.
 *
 * The access token is not the answer to that question — the refresh cookie
 * is. The cookie is httpOnly and lives for a week; the access token lives for
 * minutes and is gone the moment it expires. So "no access token" means "ask
 * the server", not "signed out". Returning `isAuthenticated: false` there was
 * why a reopened browser, or a tab restored after the token expired, landed on
 * the login screen while holding a perfectly good session.
 *
 * The silent renewal is attempted only when there is no usable token. When one
 * is present the `/auth/current-user` call proceeds directly, and the request
 * interceptor renews underneath it if the token is close to expiry.
 */
export const bootstrapAuth = createAsyncThunk("auth/bootstrap", async (_, { getState, rejectWithValue }) => {
  try {
    // The refresh cookie is httpOnly, so we cannot look for it directly. The
    // persisted `isAuthenticated` is the readable stand-in: it survives in
    // localStorage alongside the cookie and is cleared by every sign-out path.
    // Without this check every anonymous visitor to /login would pay a
    // round-trip — and a loading spinner — to be told what we already knew.
    const wasSignedIn = !!getState()?.auth?.isAuthenticated;

    if (needsRenewal(AuthService.getAccessToken())) {
      if (!wasSignedIn) return { isAuthenticated: false, user: initialUser };
      try {
        await AuthService.refresh();
      } catch {
        // There was a session and the server has now refused it. That — not a
        // missing access token — is what "signed out" actually means.
        AuthService.clearAuthStorage();
        return { isAuthenticated: false, user: initialUser };
      }
    }

    const data = await AuthService.currentUser();
    const normalized = normalizeCurrentUser(data);

    // DISABLED-FEATURE: browser extension is switched off — see server/app/api/v2/api.py.
    // // Fire-and-forget: this is the missing trigger the login-page push and
    // // the 401-refresh push don't cover — a browser tab that loads with an
    // // ALREADY-valid session (no fresh login, no token expiring) never hit
    // // either of those. Every app mount that confirms a real session pushes
    // // the JWT to the extension too, so "Connect this site" actually logs
    // // the extension in immediately instead of waiting for the next login
    // // or the next 401. No-ops silently when the extension isn't installed
    // // or isn't connected to this origin yet.
    // if (normalized.isActive) {
    //   ExtensionService.waitForExtension(800)
    //     .then((meta) => {
    //       if (!meta) {
    //         console.warn(
    //           "[TalentFlow ext] bootstrap push skipped — bridge.js not detected on this page (window.__talentflowExtension is unset). Extension not installed, or this origin hasn't been Connected yet, or the content script failed to inject.",
    //         );
    //         return null;
    //       }
    //       return ExtensionService.pushJwt(token).then((res) => {
    //         if (!res || !res.ok) {
    //           console.warn("[TalentFlow ext] bootstrap JWT push failed:", res);
    //         } else {
    //           console.log("[TalentFlow ext] bootstrap JWT push OK:", res);
    //         }
    //       });
    //     })
    //     .catch((err) => console.warn("[TalentFlow ext] bootstrap push threw:", err));
    // }

    return {
      isAuthenticated: normalized.isActive,
      user: normalized.user,
    };
  } catch (error) {
    AuthService.clearAuthStorage();
    return rejectWithValue(error?.response?.data || { message: error?.message || "Auth bootstrap failed" });
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    removeAuth: (state) => {
      AuthService.clearAuthStorage();
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isRefreshing = false;
      state.authChecked = true;
      state.error = null;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(bootstrapAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.isAuthenticated = !!action.payload.isAuthenticated;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(bootstrapAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload || action.error?.message || null;
      })

      // Two callers, two very different meanings, and they must not share a
      // flag.
      //
      // At bootstrap and at login there is no user yet, so blocking is right.
      // But this thunk is now also the reconciliation refetch: the
      // `X-Config-Version` interceptor and the `tenant_config_updated`
      // WebSocket handler dispatch it during normal use. Setting `isLoading`
      // there unmounted the entire route subtree on every SuperAdmin toggle —
      // roughly a second of blank page for every logged-in user of that
      // tenant, losing typed form input, open modals, filters, pagination and
      // scroll position, and refetching the whole page on the way back.
      //
      // `authChecked` is the honest test for "we already know who this is".
      // After it, a refetch is background work: the page keeps rendering the
      // state it has and swaps in the new permissions when they land.
      .addCase(currentUser.pending, (state) => {
        if (state.authChecked) {
          state.isRefreshing = true;
        } else {
          state.isLoading = true;
        }
      })
      .addCase(currentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.isAuthenticated = !!action.payload.isActive;
        state.user = action.payload.user;
        state.error = null;
      })
      // A failed BACKGROUND refetch keeps the session. Signing someone out
      // because one reconciliation call hit a bad minute would be a worse
      // outcome than the stale permissions it was trying to correct — and the
      // permissions are presentation only, since the server re-reads module
      // state on every request regardless. The `X-Config-Version` header comes
      // back on the next response, so the next request tries again.
      //
      // A genuinely dead token does not land here: `@/lib/api` refreshes on
      // 401 and forces a full logout when the refresh fails.
      //
      // A failed BLOCKING load clears only on 401. Every other status is the
      // call failing, not the credential: nginx throttling the read, a
      // restarting container, a dropped network. Clearing on those signed the
      // user out of EVERY tab at once — `clearAuthStorage` removes the shared
      // `localStorage` token, so each other tab hears the `storage` event and
      // drops its socket, leaving seven screens rendering data that has
      // stopped updating with no banner to say so (I1701 E1). A genuinely dead
      // token still lands here as a 401: `@/lib/api` refreshes first and only
      // gives up once the refresh itself fails.
      .addCase(currentUser.rejected, (state, action) => {
        if (state.isRefreshing) {
          // Silent: nothing changed for the user, and a stored error here
          // would surface later on a screen that reads `auth.error`.
          state.isRefreshing = false;
          return;
        }
        if (action.payload?.status !== 401) {
          state.isLoading = false;
          state.isRefreshing = false;
          state.authChecked = true;
          state.error = action.payload || action.error?.message || null;
          return;
        }
        AuthService.clearAuthStorage();
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload || action.error?.message || null;
      })

      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.isAuthenticated = !!action.payload?.isActive;
        state.user = action.payload?.user || null;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload || action.error?.message || null;
      })

      .addCase(acceptInvite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(acceptInvite.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.isAuthenticated = !!action.payload?.isActive;
        state.user = action.payload?.user || null;
        state.error = null;
      })
      .addCase(acceptInvite.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload || action.error?.message || null;
      })

      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.authChecked = true;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload || action.error?.message || null;
      });
  },
});

export const { removeAuth, clearAuthError } = authSlice.actions;
export default authSlice.reducer;
